import { motion } from "framer-motion";
import { SubpageHeader } from "../components/SubpageHeader";
import { Ornament } from "../components/Chapter";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd } from "../components/SEO";
import { porTier, TOPO, LogoWrap, logoCls } from "../data/parceiros";

const TierHeading = ({ children, testid }) => (
    <div className="flex items-center justify-center gap-4 mt-16">
        <span className="h-px flex-1 max-w-24 bg-gradient-to-l from-[#c9a24b]/50 to-transparent" />
        <h2 className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]" data-testid={testid}>{children}</h2>
        <span className="h-px flex-1 max-w-24 bg-gradient-to-r from-[#c9a24b]/50 to-transparent" />
    </div>
);

const Card = ({ p, label, sub, thanks, h }) => (
    <div className="flex flex-col items-center" data-testid={`sponsor-page-${p.id}`}>
        <div className={`flex items-end justify-center ${h.slot}`}>
            <LogoWrap p={p} testid={`sponsor-page-${p.id}-link`}>
                <img loading="lazy" decoding="async" src={p.logo} alt={p.nome} className={logoCls(h.img)} />
            </LogoWrap>
        </div>
        <p className="font-display font-light text-base text-[#efe6d6] mt-4 text-center">{p.nome}</p>
        <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#c9a24b]/80 mt-1 text-center">{label}</p>
        {sub && <p className="font-caps text-[8px] tracking-[0.2em] uppercase text-[#efe6d6]/45 mt-1 text-center">{sub}</p>}
        {thanks && <p className="font-display italic font-light text-sm text-[#efe6d6]/65 mt-3 max-w-xs mx-auto leading-relaxed text-center" data-testid={`sponsor-page-${p.id}-thanks`}>{thanks}</p>}
    </div>
);

export default function PatrocinadoresPage() {
    const { t } = useLanguage();
    const pk = t.assoc.parceiros;
    const thanks = t.patrocinadores.agradecimentos;
    useSEO({
        title: t.seo.patrocinadores.title,
        description: t.seo.patrocinadores.desc,
        jsonLd: breadcrumbLd([{ name: t.bc.home, path: "/" }, { name: t.bc.patrocinadores, path: "/patrocinadores" }]),
    });
    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative flex items-center justify-center px-6 pt-28 pb-20" data-testid="patrocinadores-page">
            <SubpageHeader />
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="text-center w-full max-w-4xl"
            >
                <div className="flex justify-center mb-8">
                    <Ornament className="w-40" />
                </div>
                <h1 className="font-caps gold-text text-2xl sm:text-3xl tracking-[0.18em]">{t.patrocinadoresPage.titulo}</h1>
                <p className="font-display italic font-light text-lg sm:text-xl text-[#efe6d6]/60 mt-8 max-w-xl mx-auto leading-relaxed">
                    {t.patrocinadoresPage.texto}
                </p>

                {/* Nível 1 — Exclusiva + Parceiro Oficial */}
                <div className="mt-14 grid sm:grid-cols-2 gap-10 items-start" data-testid="sponsor-page-topo">
                    {TOPO.map((p) => (
                        <div key={p.id} className="flex flex-col items-center" data-testid={`sponsor-page-${p.id}`}>
                            <div className="flex items-end justify-center h-52 sm:h-64">
                                <LogoWrap p={p} testid={`sponsor-page-${p.id}-link`}>
                                    <img loading="lazy" decoding="async" src={p.logo} alt={p.nome}
                                        className={`${p.tier === "exclusiva" ? "h-52 sm:h-64" : "h-44 sm:h-56"} w-auto object-contain drop-shadow-[0_0_40px_rgba(201,162,75,0.35)] group-hover:scale-[1.03] transition-transform duration-500`} />
                                </LogoWrap>
                            </div>
                            <p className="font-display font-light text-xl text-[#efe6d6] mt-5 text-center">{p.nome}</p>
                            <p className="font-caps text-[11px] tracking-[0.36em] uppercase gold-text mt-2 text-center" data-testid={`sponsor-page-${p.id}-caption`}>{pk.tiers[p.tier]}</p>
                            <p className="font-display italic font-light text-base sm:text-lg text-[#efe6d6]/75 mt-4 max-w-md mx-auto leading-relaxed" data-testid={`sponsor-page-${p.id}-thanks`}>
                                {thanks[p.agradecimentoKey]}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Nível 2 — Premium */}
                <TierHeading testid="tier-heading-premium">{pk.tiersPlural.premium}</TierHeading>
                <div className="mt-8 grid sm:grid-cols-2 gap-10" data-testid="patrocinadores-page-premium">
                    {porTier("premium").map((p) => (
                        <div key={p.id} className="flex flex-col items-center" data-testid={`sponsor-page-${p.id}`}>
                            <div className="flex items-end justify-center h-36 sm:h-40">
                                <LogoWrap p={p} testid={`sponsor-page-${p.id}-link`}>
                                    <img loading="lazy" decoding="async" src={p.logo} alt={p.nome}
                                        className={`${logoCls("h-32 sm:h-40")} drop-shadow-[0_0_30px_rgba(201,162,75,0.28)]`} />
                                </LogoWrap>
                            </div>
                            <p className="font-display font-light text-lg text-[#efe6d6] mt-5 text-center">{p.nome}</p>
                            <p className="font-caps text-[10px] tracking-[0.3em] uppercase gold-text mt-1.5 text-center" data-testid={`sponsor-page-${p.id}-caption`}>{pk.tiers.premium}</p>
                            <p className="font-display italic font-light text-base text-[#efe6d6]/75 mt-4 max-w-md mx-auto leading-relaxed" data-testid={`sponsor-page-${p.id}-thanks`}>
                                {thanks[p.agradecimentoKey]}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Nível 3 — Intermediários (com agradecimento curto) */}
                <TierHeading testid="tier-heading-intermediario">{pk.tiersPlural.intermediario}</TierHeading>
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-12" data-testid="patrocinadores-page-intermediario">
                    {porTier("intermediario").map((p) => (
                        <Card key={p.id} p={p} label={pk.tiers.intermediario} sub={p.descritorKey ? pk[p.descritorKey] : null}
                            thanks={thanks[p.agradecimentoKey]} h={{ slot: "h-24 sm:h-28", img: "h-20 sm:h-24" }} />
                    ))}
                </div>

                {/* Nível 4 — Apoiantes */}
                <TierHeading testid="tier-heading-apoiante">{pk.tiersPlural.apoiante}</TierHeading>
                <div className="mt-8 grid grid-cols-2 gap-6 max-w-md mx-auto" data-testid="patrocinadores-page-apoiante">
                    {porTier("apoiante").map((p) => (
                        <Card key={p.id} p={p} label={pk.tiers.apoiante} sub={p.descritorKey ? pk[p.descritorKey] : null}
                            h={{ slot: "h-16 sm:h-20", img: "h-14 sm:h-16" }} />
                    ))}
                </div>
            </motion.div>
        </div>
    );
}
